package dev.lingvo.site.components.automation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.MtEstimateDto
import dev.lingvo.shared.MtLocaleEstimateDto
import dev.lingvo.shared.MtPretranslateRequest
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LModalSize
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import dev.lingvo.site.state.requestWorkspaceAction
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * What a run covers, beyond the languages it targets.
 *
 * Every field is a filter the server already understands, so a caller states its scope once and the
 * *same* object is priced and then posted — see [PretranslateConfirm]. A surface that rebuilt the
 * request between the estimate and the start could price one run and charge for another.
 *
 * @param provider null means "whichever provider this project has enabled", resolved here rather
 *   than guessed by each caller.
 */
data class PretranslateScope(
    val onlyEmpty: Boolean = true,
    val tag: String? = null,
    val fileId: String? = null,
    val keyIds: List<String> = emptyList(),
    val memoryOnly: Boolean = false,
    val tone: String? = null,
    val provider: MtProvider? = null,
)

/**
 * Everything the dialog needs to price a run and decide whether to offer it.
 *
 * Loaded as one unit so the confirm never draws a price beside a button whose authority has not
 * been answered yet.
 */
internal data class PretranslatePlan(
    val projectName: String,
    val canSpend: Boolean,
    val provider: MtProvider?,
    val request: MtPretranslateRequest?,
    val estimate: MtEstimateDto?,
)

/**
 * The one confirmation between "pre-translate this" and money leaving the workspace.
 *
 * **Estimate before, receipt after** (spec §2.5). The number in this dialog is the server's own
 * arithmetic — `GET /mt/estimate` and `POST /mt/pretranslate` are produced by the same `planMtJob`
 * — and it is fetched through the **request-object overload**, passing the exact
 * [MtPretranslateRequest] the confirm button will post. That is the only mechanical guarantee that
 * the priced run and the started run are the same run; a surface that rebuilt the request by hand
 * between the two would be free to disagree with itself.
 *
 * **Absent, not disabled, when the reader may not spend.** `canSpendCredits` is answered by the
 * server (role ≥ ADMIN *and* the credential carries `mt:spend`), because the token half of that pair
 * is invisible to the browser — inferring it from the role renders a button that 403s. When it is
 * false the dialog states who does hold the privilege instead of offering a control that will be
 * refused.
 *
 * **A duplicate press is not an error.** The server answers `409 job_already_running` with the run
 * that is already going in `details.jobId`; that is a run to attach to, so it is reported through
 * [onStarted] exactly as a fresh one is.
 *
 * The route ignores `tone`, so the estimate does not cover a tone choice and this dialog does not
 * claim it does.
 *
 * @param locales the target languages, source excluded by the caller.
 * @param onStarted the run that is now going — new, or the one a 409 named.
 */
@Composable
fun PretranslateConfirm(
    open: Boolean,
    slug: String,
    locales: List<String>,
    scope: PretranslateScope = PretranslateScope(),
    onStarted: (jobId: String) -> Unit,
    onClose: () -> Unit,
) {
    // Mounted only while open, so the estimate is requested when somebody asks for a price and
    // never on the chance that they might. The exit is immediate on purpose: a confirmation that
    // has already acted has nothing left to say, and spec §2.9 keeps new motion off surfaces that
    // sit beside typing.
    if (!open) return
    PretranslateConfirmBody(slug, locales, scope, onStarted, onClose)
}

/**
 * The same confirmation for a caller that has already built the exact run.
 *
 * The scope overload above assembles an [MtPretranslateRequest] from a language list and resolves
 * the provider itself, which is what a surface starting a run *from a count* wants. A surface that
 * derives its run from something richer than a count — the editor's queue, whose filters decide the
 * request and whose own primacy rules decide whether to offer it at all — has already built that
 * object, and rebuilding it here from a projection of itself is the one thing the estimate/receipt
 * guarantee cannot survive: two constructions of "the same" run are free to disagree.
 *
 * So this overload constructs nothing. The instance handed in is the instance priced by
 * `GET /mt/estimate` and the instance posted to `POST /mt/pretranslate`, and the provider shown in
 * the estimate is [MtPretranslateRequest.provider] rather than whichever provider a fresh lookup
 * would have found. Everything else — the authority claim, the 409 attach, the refusal copy — is
 * the surface both overloads share.
 */
@Composable
fun PretranslateConfirm(
    open: Boolean,
    slug: String,
    request: MtPretranslateRequest,
    onStarted: (jobId: String) -> Unit,
    onClose: () -> Unit,
) {
    if (!open) return
    PretranslateRequestBody(slug, request, onStarted, onClose)
}

@Composable
private fun PretranslateConfirmBody(
    slug: String,
    locales: List<String>,
    scope: PretranslateScope,
    onStarted: (String) -> Unit,
    onClose: () -> Unit,
) {
    val sortedLocales = remember(locales) { locales.distinct().sorted() }
    val planState = loadAsync(slug, sortedLocales, scope) {
        val project = ApiClient.getProject(slug)
        val provider = scope.provider ?: ApiClient.listProviders(slug)
            .firstOrNull(ProviderSettingsDto::enabled)
            ?.provider
        val request = provider?.let {
            MtPretranslateRequest(
                provider = it,
                locales = sortedLocales,
                onlyEmpty = scope.onlyEmpty,
                tone = scope.tone,
                tag = scope.tag,
                fileId = scope.fileId,
                keyIds = scope.keyIds,
                memoryOnly = scope.memoryOnly,
            )
        }
        PretranslatePlan(
            projectName = project.name,
            canSpend = project.editorCapabilities?.canSpendCredits == true,
            provider = provider,
            request = request,
            // The same object that will be posted, priced by the server that will charge for it.
            estimate = request?.let { ApiClient.mtEstimate(slug, it) },
        )
    }
    PretranslateConfirmSurface(slug, planState, onStarted, onClose)
}

@Composable
private fun PretranslateRequestBody(
    slug: String,
    request: MtPretranslateRequest,
    onStarted: (String) -> Unit,
    onClose: () -> Unit,
) {
    val planState = loadAsync(slug, request) {
        val project = ApiClient.getProject(slug)
        PretranslatePlan(
            projectName = project.name,
            canSpend = project.editorCapabilities?.canSpendCredits == true,
            provider = request.provider,
            request = request,
            // The caller's own instance, priced and then posted. Nothing between the two rebuilds it.
            estimate = ApiClient.mtEstimate(slug, request),
        )
    }
    PretranslateConfirmSurface(slug, planState, onStarted, onClose)
}

@Composable
private fun PretranslateConfirmSurface(
    slug: String,
    planState: Async<PretranslatePlan>,
    onStarted: (String) -> Unit,
    onClose: () -> Unit,
) {
    val coroutineScope = rememberCoroutineScope()
    var starting by remember { mutableStateOf(false) }
    var failure by remember { mutableStateOf<String?>(null) }

    val plan = (planState as? Async.Success)?.value
    val estimate = plan?.estimate
    val blocked = estimate?.blockedReason

    // The claim is captured from whatever authority this surface can actually verify, before any
    // suspension, and re-checked on both sides of the request. On a project route that is the
    // verified project itself, under `ProjectPrivilege.SPEND`; from a workspace surface — the
    // dashboard's attention card, the palette on `/app` — it is the selected workspace's admin bar
    // under its own name, `WorkspacePrivilege.MT_SPEND`. Both resolve to owner-or-admin, which is
    // the bar the spend routes apply, and both are named for the spend rather than borrowed from
    // the structure/provider privileges that happen to sit at the same height.
    val projectClaim = remember(slug, Session.requestIdentity) {
        ProjectActionClaim.capture(
            route = liveProjectRoute(slug),
            memberships = Session.organizations,
            privilege = ProjectPrivilege.SPEND,
        )
    }
    val workspaceClaim = remember(projectClaim, Session.requestIdentity) {
        if (projectClaim != null) {
            null
        } else {
            WorkspaceActionClaim.capture(WorkspaceRequestContext.current(), WorkspacePrivilege.MT_SPEND)
        }
    }
    val authorized = projectClaim != null || workspaceClaim != null

    val start: () -> Unit = start@{
        val request = plan?.request ?: return@start
        if (starting) return@start
        coroutineScope.launch {
            starting = true
            failure = null
            val post: suspend () -> String = { ApiClient.pretranslate(slug, request) }
            try {
                val jobId = when {
                    projectClaim != null -> requestProjectAction(
                        claim = projectClaim,
                        currentRoute = { liveProjectRoute(slug) },
                        currentMemberships = { Session.organizations },
                        transport = post,
                    )

                    workspaceClaim != null -> requestWorkspaceAction(
                        claim = workspaceClaim,
                        currentContext = { WorkspaceRequestContext.current() },
                    ) { post() }

                    else -> null
                }
                if (jobId != null) {
                    onStarted(jobId)
                    onClose()
                }
            } catch (cancelled: CancellationException) {
                // Authority loss and navigation both arrive here. Neither is a message: the surface
                // that asked for the run is already gone or is no longer allowed to have asked.
                throw cancelled
            } catch (error: Throwable) {
                val running = error.runningJobId()
                if (running != null) {
                    onStarted(running)
                    onClose()
                } else {
                    failure = (error as? ApiException)?.message?.takeIf(String::isNotBlank)
                        ?: tr(
                            "automation.pretranslate_confirm.couldnt_start_the_run_check_the",
                            "Couldn't start the run. Check the provider and try again.",
                        )
                }
            } finally {
                starting = false
            }
        }
    }

    PretranslateConfirmDialog(
        planState = planState,
        authorized = authorized,
        starting = starting,
        failure = failure,
        onStart = start,
        onClose = onClose,
    )
}

/**
 * The confirmation itself, with the run offered only when this dialog can actually start one.
 *
 * **A confirm button is not offered for a run that cannot be started** (spec §2.6). Three states
 * reach this surface with nothing to post: no provider is connected (`plan.request == null`), the
 * reader may not spend (`canSpend == false`), and neither authority claim holds (`authorized ==
 * false`) — and the estimate is still in flight in a fourth. A confirm rendered in any of them is a
 * live accent button whose handler returns on its first line: no request, no error, no close, no
 * busy state. The only reading available to somebody who presses it is that the product is broken,
 * so they press it again.
 *
 * Each of those states already says what is wrong in the dialog's body, and that alert is now the
 * whole of what the dialog offers. It is one [LModal] rather than a confirm swapped for a notice,
 * because the plan settles *while the dialog is open*: two components would unmount the overlay and
 * mount another one under the reader on every open, replaying the scrim and dropping focus.
 *
 * @param authorized whether either captured claim can carry the spend — see the capture in
 *   `PretranslateConfirmSurface`, which is the only place that can verify it.
 */
@Composable
internal fun PretranslateConfirmDialog(
    planState: Async<PretranslatePlan>,
    authorized: Boolean,
    starting: Boolean,
    failure: String?,
    onStart: () -> Unit,
    onClose: () -> Unit,
) {
    val plan = (planState as? Async.Success)?.value
    val estimate = plan?.estimate
    val blocked = estimate?.blockedReason
    val palette = ColorMode.current.toSitePalette()
    // Every conjunct is a thing the server checks. A run is offered when all four hold and is
    // absent — not disabled — otherwise, beside the sentence that says which one failed.
    val offerRun = plan != null && plan.request != null && plan.canSpend && authorized
    val close = { if (!starting) onClose() }

    LModal(
        open = true,
        title = t("automation.pretranslate_confirm.pre_translate", "Pre-translate"),
        onClose = close,
        size = LModalSize.SM,
        actions = {
            LButton(
                t("automation.pretranslate_confirm.cancel", "Cancel"),
                close,
                variant = LButtonVariant.GHOST,
                enabled = !starting,
            )
            if (offerRun) {
                LButton(
                    t("automation.pretranslate_confirm.start_the_run", "Start the run"),
                    onStart,
                    // Nothing here is destructive: a run writes TRANSLATED, which is reviewable. The
                    // one accent stays where it belongs and an approve-shaped action does not borrow
                    // danger's colour.
                    variant = LButtonVariant.PRIMARY,
                    enabled = !starting,
                    loading = starting,
                )
            }
        },
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            when {
                planState is Async.Loading -> LSkeleton(
                    label = t("automation.pretranslate_confirm.pricing_this_run", "Pricing this run"),
                )

                planState is Async.Failure -> LAlert(
                    title = t(
                        "automation.pretranslate_confirm.couldnt_price_this_run",
                        "Couldn't price this run",
                    ),
                    description = planState.readable(),
                    variant = LAlertVariant.DANGER,
                )

                plan == null -> Unit

                !plan.canSpend -> LAlert(
                    title = t(
                        "automation.pretranslate_confirm.only_an_owner_or_admin_can",
                        "Only an owner or admin can start a run",
                    ),
                    description = t(
                        "automation.pretranslate_confirm.a_run_spends_this_workspaces_credits",
                        "A run spends this workspace's credits. You can still translate and " +
                            "review every string it produces.",
                    ),
                    variant = LAlertVariant.INFO,
                )

                plan.request == null -> LAlert(
                    title = t(
                        "automation.pretranslate_confirm.no_provider_is_connected",
                        "No provider is connected",
                    ),
                    description = t(
                        "automation.pretranslate_confirm.connect_a_translation_provider_before_starting",
                        "Connect a translation provider before starting a run.",
                    ),
                    variant = LAlertVariant.WARNING,
                )

                else -> {
                    blocked?.let {
                        LAlert(
                            title = t(
                                "automation.pretranslate_confirm.this_run_cannot_start",
                                "This run cannot start",
                            ),
                            description = it,
                            variant = LAlertVariant.WARNING,
                        )
                    }
                    if (estimate != null) {
                        EstimateBody(plan.provider, plan.projectName, estimate)
                    }
                    if (!authorized) {
                        LAlert(
                            title = t(
                                "automation.pretranslate_confirm.open_the_project_to_start_this",
                                "Open the project to start this run",
                            ),
                            description = t(
                                "automation.pretranslate_confirm.this_workspace_isnt_the_one_that",
                                "This workspace isn't the one that owns the project any more, so " +
                                    "the run cannot be started from here.",
                            ),
                            variant = LAlertVariant.WARNING,
                        )
                    }
                }
            }
            // The initiating control is the only thing that goes busy, and the sentence beside it
            // is what a reader who cannot see the spinner has. `role="status"` as well as
            // `aria-live`: the role is what makes this a region assistive technology can find.
            if (starting) {
                Span(
                    Modifier
                        .color(palette.textSecondary)
                        .fontSize(TypeScale.Small)
                        .toAttrs {
                            attr("role", "status")
                            attr("aria-live", "polite")
                        },
                ) {
                    Text(t("automation.pretranslate_confirm.starting_the_run", "Starting the run"))
                }
            }
            // A refusal from the post lands here rather than closing the dialog: the run did not
            // start, the estimate above is still the estimate, and the control that retries it is
            // still where it was.
            failure?.let { message ->
                LAlert(
                    title = t("automation.pretranslate_confirm.nothing_started", "Nothing started"),
                    description = message,
                    variant = LAlertVariant.DANGER,
                    live = true,
                )
            }
        }
    }
}

/**
 * The priced scope, per language, and what it leaves behind.
 *
 * Counts rather than percentages, and the credits line names what the workspace has left after the
 * run rather than only what the run costs — "412 credits" answers nothing on its own.
 */
@Composable
private fun EstimateBody(provider: MtProvider?, projectName: String, estimate: MtEstimateDto) {
    val palette = ColorMode.current.toSitePalette()
    val strings = estimate.perLocale.sumOf(MtLocaleEstimateDto::providerBound)
    val fromMemory = estimate.perLocale.sumOf(MtLocaleEstimateDto::memoryPrefillable)
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        Span(Modifier.fontSize(TypeScale.BodySmall).color(palette.textSecondary).toAttrs()) {
            Text(
                tf(
                    "automation.pretranslate_confirm.provider_will_translate_in_project",
                    "{provider} will translate in {project}.",
                    "provider" to (provider?.displayName ?: ""),
                    "project" to projectName,
                ),
            )
        }
        estimate.perLocale.forEach { row ->
            EstimateRow(
                label = localeInfo(row.locale).englishName,
                value = tPlural(
                    "automation.pretranslate_confirm.count.string",
                    row.providerBound,
                    "{count} string",
                    "{count} strings",
                ),
            )
        }
        if (fromMemory > 0) {
            EstimateRow(
                label = t(
                    "automation.pretranslate_confirm.filled_from_memory",
                    "Filled from memory, free",
                ),
                value = formatCount(fromMemory),
            )
        }
        EstimateRow(
            label = t("automation.pretranslate_confirm.strings_to_send", "Strings to send"),
            value = formatCount(strings),
            strong = true,
        )
        if (estimate.byok) {
            EstimateRow(
                label = t("automation.pretranslate_confirm.credits", "Credits"),
                value = t(
                    "automation.pretranslate_confirm.none_this_project_uses_its_own",
                    "None — this project uses its own provider key",
                ),
                strong = true,
            )
        } else {
            EstimateRow(
                label = t("automation.pretranslate_confirm.credits_this_run", "Credits this run"),
                value = formatCount(estimate.totalCredits),
                strong = true,
            )
            EstimateRow(
                label = t("automation.pretranslate_confirm.credits_left_after", "Credits left after"),
                value = formatCount(
                    (estimate.creditsRemaining - estimate.totalCredits).coerceAtLeast(0),
                ),
            )
        }
    }
}

@Composable
private fun EstimateRow(label: String, value: String, strong: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    Row(
        Modifier.fillMaxWidth().gap(Space.MD),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Span(
            Modifier
                .flexGrow(1)
                .fontSize(TypeScale.BodySmall)
                .color(if (strong) palette.textPrimary else palette.textSecondary)
                .toAttrs(),
        ) {
            Text(label)
        }
        Span(
            Modifier
                .tabularNumbers()
                .fontSize(TypeScale.BodySmall)
                .fontWeight(if (strong) 600 else 400)
                .color(palette.textPrimary)
                .toAttrs(),
        ) {
            Text(value)
        }
    }
}

/**
 * The run a `409 job_already_running` is pointing at.
 *
 * `details.jobId` is a run to attach to, not an error to display — the reader asked for work that
 * is already happening, so the honest answer is the receipt for it.
 */
internal fun Throwable.runningJobId(): String? {
    val api = this as? ApiException ?: return null
    if (api.status != 409 || api.code != "job_already_running") return null
    return api.details["jobId"]?.jsonPrimitive?.content?.takeIf(String::isNotBlank)
}
